import { useState, useEffect } from "react";
import { DynamicFieldItem } from "../../../../../model/dynamic-forms";
import { SectionDefinition } from "../../../../../model/service-request/section-definition";
import { FieldType } from "../../../../../model/service-request/attribute";
import { AdminFieldGenerator } from "./AdminFieldGenerator";
import { isAgentRenderableAttribute, isAgentVisibleAttribute } from "../../context/ServiceRequestFormContext";
import styles from "./AdminDynamicForm.module.scss";

interface Props {
    sectionDefinition: SectionDefinition;
    preselectedFormValues?: DynamicFieldItem[];
    forceValidate?: boolean;
    readOnlyFieldIds?: Set<string>;
    formStateChanged?: (fieldValues: DynamicFieldItem[]) => void;
}

const buildInitialFieldValues = (sectionDefinition: SectionDefinition, preselectedFormValues: DynamicFieldItem[]): DynamicFieldItem[] =>
    sectionDefinition.attributeDefinitions
        .filter(isAgentRenderableAttribute)
        .map((attr) => {
            const existing = preselectedFormValues.find((f) => f.fieldId === attr.id);
            if (existing) return existing;
            return {
                fieldId: attr.id,
                fieldLabel: attr.label,
                fieldValue: attr.fieldType === FieldType.Bool ? (attr.defaultState ?? false) : null,
                fieldType: attr.fieldType!,
                fieldPath: `${attr.id}.value`,
            };
        });

const AdminDynamicForm = ({ sectionDefinition, preselectedFormValues = [], forceValidate, readOnlyFieldIds, formStateChanged }: Props) => {
    const [fieldValues, setFieldValues] = useState<DynamicFieldItem[]>(() =>
        buildInitialFieldValues(sectionDefinition, preselectedFormValues)
    );

    // Hidden fields are rendered for the agent's reference only — strip them
    // from what's reported upward so the submitted payload contains exactly
    // the customer-visible field set (the backend stamps hidden values itself
    // and rejects payloads that include hidden-only sections).
    const visibleFieldIds = new Set(
        sectionDefinition.attributeDefinitions.filter(isAgentVisibleAttribute).map((a) => a.id)
    );
    const reportFieldValues = (fields: DynamicFieldItem[]) =>
        formStateChanged?.(fields.filter((f) => visibleFieldIds.has(f.fieldId)));

    useEffect(() => {
        reportFieldValues(fieldValues);
    }, []);
    const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (forceValidate) {
            setTouchedFields(new Set(sectionDefinition.attributeDefinitions.map((a) => a.id!)));
        }
    }, [forceValidate]);

    const handleFieldChanged = (value: DynamicFieldItem) => {
        const updated = [...fieldValues];
        const idx = updated.findIndex((f) => f.fieldId === value.fieldId);
        if (idx !== -1) updated[idx] = value;
        else updated.push(value);

        setFieldValues(updated);
        setTouchedFields((prev) => new Set(prev).add(value.fieldId));
        reportFieldValues(updated);
    };

    const handleFieldReset = (value: DynamicFieldItem) => {
        const updated = [...fieldValues];
        const idx = updated.findIndex((f) => f.fieldId === value.fieldId);
        if (idx !== -1) updated[idx] = value;
        else updated.push(value);

        setFieldValues(updated);
        reportFieldValues(updated);
    };

    const getFieldError = (fieldId: string, isRequired?: boolean): string | undefined => {
        if (!isRequired || (!touchedFields.has(fieldId) && !forceValidate)) return undefined;
        const val = fieldValues.find((f) => f.fieldId === fieldId)?.fieldValue;
        return val === "" || val === null || val === undefined ? "This field is required" : undefined;
    };

    const enabledFields = sectionDefinition.attributeDefinitions
        .filter(isAgentRenderableAttribute)
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

    // Sections whose attributes are all disabled (e.g. "Address Information",
    // "Additional Information") would render as a bare title — skip them.
    if (enabledFields.length === 0) return null;

    return (
        <div className={styles.sectionContainer}>
            {sectionDefinition.name && <h4 className={styles.sectionTitle}>{sectionDefinition.name}</h4>}
            <div className={styles.fieldsGrid}>
                {enabledFields.map((attr) => (
                    <AdminFieldGenerator
                        key={attr.id}
                        attributeDefinition={attr}
                        fieldValueChanged={handleFieldChanged}
                        fieldValueReset={handleFieldReset}
                        preselectedFormValue={fieldValues.find((f) => f.fieldId === attr.id)}
                        error={attr.isHidden === true ? undefined : getFieldError(attr.id!, attr.isRequired)}
                        readOnly={(readOnlyFieldIds?.has(attr.id!) ?? false) || attr.isHidden === true}
                    />
                ))}
            </div>
        </div>
    );
};

export default AdminDynamicForm;
